import { loadScriptAssets, loadStyleAssets } from '@merkur/integration';
import { getMerkur } from '@merkur/core';
import { pushIdentity } from '@iva/syncidentity';
import { Circle, Infinite } from 'infinite-circle';

import { getTopicSettings } from '../config';
import { discussionFetchCommentCount } from '../commentCount/main';
import { BREAKPOINT } from './DeviceHelper';
import EmbedEvent from './event/EmbedEvent';
import EmbedScriptEvent from './event/EmbedScriptEvent';
import DiscussionInstanceEvent from './event/DiscussionInstanceEvent';
import scrollToElement from './interface/scrollToElement';
import getWindowViewportRect from './interface/getWindowViewportRect';
import EventEmitter from './interface/EventEmitter';
import { processTokenError, getLocalStorageData, isSafeRedirectUrl } from './Utils';
import { AddCommentButton } from './AddCommentButton';

const WRAPPER_STYLE = 'display: block;overflow: hidden;position: relative;';
const COMPONENT_ROOT_CLASS = 'szn-component-root';

const scrollCircle = new Circle({
	listen: notify => window.addEventListener('scroll', notify),
	unlisten: notify => window.removeEventListener('scroll', notify)
});

const infinite = new Infinite();
infinite.add(scrollCircle);

export default class Discussion extends EventEmitter {
	constructor(instanceId, service, element, config, authorizationTokenProvider) {
		super();

		this.instanceId = instanceId;

		this.isLoaded = false;
		this.loadListener = null;

		this._service = service;
		this._wrapper = element;
		this._config = config;
		this._authorizationTokenProvider = authorizationTokenProvider;

		this.iframe = null;
		this._skinStyleElement = null;

		this._wrapper.innerHTML = '';
		this._wrapper.style = WRAPPER_STYLE;

		this._componentRoot = this._createComponentRoot();
		this._componentShadowRoot = this._createComponentShadowRoot(this._componentRoot);
		this._instanceCircleId = this._registerScrollCircle();
		this._userIdentityElement = null;
	}

	get onLoad() {
		return this.loadListener;
	}

	set onLoad(onLoad) {
		this.loadListener = onLoad;

		if (onLoad && this.isLoaded) {
			setTimeout(() => {
				if (this.loadListener === onLoad) {
					onLoad();
				}
			}, 0);
		}
	}

	handleDocumentResize() {
		this._updateBoundingRectInIframe(
			getWindowViewportRect(),
			this._getIframeBoundingClientRect()
		);
	}

	[EmbedEvent.REQUEST_SOCIAL_TOKEN](payload) {
		const lastToken = this._authorizationTokenProvider.lastAuthorizationToken;

		if (lastToken) {
			this.sendEvent(EmbedScriptEvent.LOGIN, {
				token: lastToken,
				sid: this._getIdentities().sid,
				isLoginWithoutUserAction: payload?.isLoginWithoutUserAction
			});
		} else if (this._authorizationTokenProvider.isSignedIn()) {
			this._authorizationTokenProvider.getToken((tokenError, token) => {
				this._authorizationTokenProvider.lastAuthorizationToken = token;

				if (token) {
					this.sendEvent(EmbedScriptEvent.LOGIN, {
						token,
						sid: this._getIdentities().sid,
						isLoginWithoutUserAction: payload?.isLoginWithoutUserAction
					});
				}

				if (tokenError) {
					processTokenError(tokenError);

					const identities = this._getIdentities();
					this.sendEvent(EmbedScriptEvent.NO_TOKEN_TO_SEND, {
						sid: identities.sid,
						login: identities.login
					});
				}
			});
		} else {
			const identities = this._getIdentities();
			this.sendEvent(EmbedScriptEvent.NO_TOKEN_TO_SEND, {
				sid: identities.sid,
				login: identities.login
			});
		}
	}

	[EmbedEvent.CONTENT_CHANGED](payload) {
		this.fire(DiscussionInstanceEvent.CONTENT_CHANGED, payload);
	}

	[EmbedEvent.ERROR]({ error }) {
		this.fire(DiscussionInstanceEvent.ERROR, { error });
	}

	[EmbedEvent.IDENTITY_FWD]({ sid, said }) {
		pushIdentity({ sid, said });
	}

	[EmbedEvent.LINK_REDIRECT]({ url }) {
		if (!isSafeRedirectUrl(url)) {
			return;
		}
		if (this._config.linksInNewTab) {
			window.open(url, '_blank');
		} else {
			window.location.assign(url);
		}
	}

	[EmbedEvent.LOGIN]() {
		this.fire(DiscussionInstanceEvent.LOGIN);
	}

	[EmbedEvent.RESIZE](payload) {
		this.iframe.style.height = payload.height + 'px';

		this._updateBoundingRectInIframe(
			getWindowViewportRect(),
			this._getIframeBoundingClientRect()
		);

		this.fire(DiscussionInstanceEvent.RESIZED, { height: payload.height });
	}

	[EmbedEvent.UPDATE_BADGE]() {
		window && window.login && window.login.badge && window.login.badge.update();
	}

	[EmbedEvent.SCROLL]({ scrollOffset, alignment, durationInMs, cancelOnUserScroll }) {
		if (!this._config.allowScroll) {
			return;
		}

		const onCompleteCallback = () => {
			this.fire(DiscussionInstanceEvent.SCROLLED);
			this.sendEvent(EmbedScriptEvent.SCROLL_DONE);
		};

		scrollToElement({
			element: this.iframe,
			alignment,
			additionalOffset: scrollOffset,
			durationInMs,
			cancelOnUserScroll,
			onCompleteCallback
		});
	}

	[EmbedEvent.SENTINEL_HANDSHAKE_ACK](payload) {
		if (this.instanceId !== payload.sentinel) {
			return;
		}

		this.isLoaded = true;
		this.fire(DiscussionInstanceEvent.LOADED);
		this.sendEvent(EmbedScriptEvent.HANDSHAKE_OK, {
			isStagingBuild: this._config.isStagingBuild
		});

		if (this.onLoad) {
			this.onLoad();
		}
	}

	[EmbedEvent.SKIN_COLORS_FWD](colorTheme) {
		if (this._skinStyleElement) {
			return;
		}

		let cssVariables = '';

		for (const variable in colorTheme) {
			if (!colorTheme[variable]) {
				continue;
			}

			const cssVariable = variable
				.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2')
				.toLowerCase();

			cssVariables += `--szn--skin-${cssVariable}: ${colorTheme[variable]};`;
		}

		if (!cssVariables.length) {
			return;
		}

		this._skinStyleElement = document.createElement('style');
		this._skinStyleElement.innerHTML = `:host { ${cssVariables} }`;

		this._componentShadowRoot.appendChild(this._skinStyleElement);
	}

	/**
	 * add items to LS and set X minutes expire
	 * @param item[] items - items to set inside LS. Item must contains key value pair
	 * @param bool reload - if reload page after insert items to LS
	 * @param int expire - set expiration of item in milliseconds
	 */
	[EmbedEvent.SET_LOCAL_STORAGE_ITEMS]({
		items = [],
		reload,
		expire = 5 * 60 * 1000,
		pureItem = []
	}) {
		try {
			const now = new Date();

			items.forEach(item => {
				window.localStorage.setItem(
					item.key,
					JSON.stringify({
						value: JSON.parse(item.value),
						expire: now.getTime() + expire
					})
				);
			});

			pureItem &&
				pureItem.forEach(item => {
					window.localStorage.setItem(item.key, item.value);
				});

			reload && window.location.reload();
		} catch (error) {
			console.error('failed to set local storage items', error);
		}
	}

	/**
	 * remove items from LS
	 * @param string[] items - remove items from LS
	 * @param bool reload - if reload page after insert items to LS
	 */
	[EmbedEvent.REMOVE_LOCAL_STORAGE_ITEMS]({ items, reload }) {
		try {
			items.forEach(value => {
				window.localStorage.removeItem(value);
			});

			reload && window.location.reload();
		} catch (error) {
			console.error('failed to remove local storage items');
		}
	}

	[EmbedEvent.OPEN_OAUTH_LOGIN]() {
		try {
			window.SznOauthTokenProvider.signIn(isSignInDialogShown => {
				if (!isSignInDialogShown) {
					this.sendEvent(EmbedScriptEvent.OAUTH_FAIL);
				}
			});
		} catch (error) {
			console.error('discusssion oauth login failed');
			this.sendEvent(EmbedScriptEvent.OAUTH_FAIL);
		}
	}

	[EmbedEvent.RENDER_ADD_BUTTON](payload) {
		if (
			this.addCommentButton &&
			payload?.addTextareaMock === this.addCommentButton.addTextareaMock
		) {
			return;
		}
		// destroy previous addButton if exists, to render new with current payload config.
		this.addCommentButton && this.addCommentButton.destroy();

		this.addCommentButton = new AddCommentButton(this, payload?.addTextareaMock);

		const visibilityData = {
			window: getWindowViewportRect(),
			iframe: this._getIframeBoundingClientRect()
		};

		this.addCommentButton.render(payload?.styles, visibilityData);
	}

	[EmbedEvent.OPEN_LOGIN]({ openConfig }) {
		try {
			if (openConfig) {
				login.open(openConfig);
			} else {
				login.open();
			}
		} catch (error) {
			console.error('discusssion login failed');
			this.sendEvent(EmbedScriptEvent.LOGIN_FAIL);
		}
	}

	[EmbedEvent.USER_IDENTITY]({ attributes }) {
		if (this._userIdentityElement) {
			this._setAttributes(this._userIdentityElement, attributes);
		}
	}

	[EmbedEvent.WIDGET]({ widget, data }) {
		const widgetUrl = new URL(`${this._config.widgetUrl}${widget}`);

		return fetch(widgetUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json'
			},
			body: JSON.stringify(data)
		})
			.then(response => response.json())
			.then(data => {
				Object.assign(data.props, {
					containerSelector: `.${COMPONENT_ROOT_CLASS}`,
					sendEvent: (type, payload = {}) => this.sendEvent(type, payload)
				});

				return data;
			})
			.then(async widgetProperties => {
				// attach root to dom
				// (has to be done before loading assets, otherwise they will never finish loading)
				document.body.appendChild(this._componentRoot);

				// load widget assets
				await loadScriptAssets(widgetProperties.assets);
				await loadStyleAssets(widgetProperties.assets, this._componentShadowRoot);

				// create instance of merkur widget
				const widget = await getMerkur().create(widgetProperties);

				// alive merkur widget
				return widget.mount(this._componentShadowRoot);
			})
			.catch(err => {
				console.error(err);
				return null;
			});
	}

	loadDiscussion(canonicalString, metaData, analyticsData, settings = {}) {
		let oauthSettings = {};
		if (window && !!window.SznOauthTokenProvider) {
			oauthSettings = { oauthLogin: true };
		}
		const preferentSort = getLocalStorageData('_szn_discussion_local_sort')
			?.localSort;

		this.adjustIframeHeightBasedOnCommentsCount(canonicalString);
		const discussionUrl = this._constructIframeUrl(['discussion', canonicalString], {
			'discussion--metaData': metaData,
			'page--analyticsData': analyticsData,
			'page--settings': Object.assign(
				{},
				oauthSettings,
				getTopicSettings(),
				settings,
				{ localSort: preferentSort }
			)
		});

		this._createIframe(discussionUrl);
		this._createUserIdentityElement(metaData);
	}

	adjustIframeHeightBasedOnCommentsCount(canonicalString) {
		const INITIAL_WIDTH = getWindowViewportRect().width;
		// Aproximate height of two-liner comment
		const commentHeightLargeDevice = 150;
		const commentHeightSmallDevice = 170;
		//MAX - maximum initial height of the iframe
		const MAX_COMMETS_COUNT_TO_CAHNGE_HEIGHT = 10; // we just need to fill the user viewport
		const defaultHeight = INITIAL_WIDTH < BREAKPOINT.SMALL ? 230 : 180; // aproximate height of input and footer in empty discussion
		const commentHeight =
			INITIAL_WIDTH < BREAKPOINT.MEDIUM
				? commentHeightSmallDevice
				: commentHeightLargeDevice;

		discussionFetchCommentCount([canonicalString]).then(result => {
			const iframeHeight =
				defaultHeight +
				Math.min(
					MAX_COMMETS_COUNT_TO_CAHNGE_HEIGHT,
					result[canonicalString].commentCount
				) *
					commentHeight;
			this.iframe.style = `display: block; position: relative; width: 100%; min-height: ${iframeHeight}px`;
		});
	}

	loadThread(commentId, analyticsData, settings = {}) {
		const threadUrl = this._constructIframeUrl(['thread', commentId], {
			'page--analyticsData': analyticsData,
			'page--settings': settings
		});

		this._createIframe(threadUrl);
	}

	loadReplies(commentId, fromCommentId, analyticsData, settings = {}) {
		const threadUrl = this._constructIframeUrl(
			['replies', commentId, fromCommentId],
			{
				'page--analyticsData': analyticsData,
				'page--settings': settings
			}
		);

		this._createIframe(threadUrl);
	}

	loadConversation(commentId, analyticsData, settings = {}) {
		const threadUrl = this._constructIframeUrl(['conversation', commentId], {
			'page--analyticsData': analyticsData,
			'page--settings': settings
		});

		this._createIframe(threadUrl);
	}

	sendEvent(type, payload = {}) {
		// the root is removed only for aesthetic reasons, as the merkur widget is unmounted anyway
		if (type === EmbedScriptEvent.MODAL_CLOSED) {
			this._componentRoot.remove();

			this._componentRoot = this._createComponentRoot();
			// shadow root must be recreated because it keeps previous widget elements.
			this._componentShadowRoot = this._createComponentShadowRoot(
				this._componentRoot
			);
		}

		if (!this.iframe || !this.iframe.contentWindow) {
			return;
		}

		this.iframe.contentWindow.postMessage(
			{
				sentinel: this.instanceId,
				type,
				payload
			},
			'*'
		);
	}

	unload() {
		scrollCircle.unregister(this._instanceCircleId);

		if (this.addCommentButton) {
			this.addCommentButton.destroy();
		}

		this._destroyIframe();
	}

	_createComponentRoot() {
		const componentRoot = document.createElement('div');
		componentRoot.className = COMPONENT_ROOT_CLASS;
		return componentRoot;
	}

	_createComponentShadowRoot(shadowHost) {
		//IE does no support `attachShadow` method. So for IE we consider regular root as shadow host
		try {
			return shadowHost.attachShadow({ mode: 'open' });
		} catch (e) {
			return shadowHost;
		}
	}

	_createIframe(srcUrl) {
		if (!this.iframe) {
			this.iframe = document.createElement('iframe');

			this._wrapper.appendChild(this.iframe);
		}

		this.iframe.scrolling = 'no';
		this.iframe.allow = 'clipboard-write *';
		this.iframe.style =
			'display: block;position: relative;width: 100%; min-height: 300px';
		this.iframe.src = srcUrl;

		return this.iframe;
	}

	_createUserIdentityElement(metaData) {
		const script = document.createElement('script');
		script.src = this._config.userIdentityScriptUrl;
		document.body.appendChild(script);

		this._userIdentityElement = document.createElement('szn-user-identity');
		this._setAttributes(this._userIdentityElement, {
			'dialog-mode': 'manual',
			'return-url': metaData?.url || window.location.href
		});
		document.body.appendChild(this._userIdentityElement);
	}

	_setAttributes(element, attributes) {
		for (let key in attributes) {
			element.setAttribute(key, attributes[key]);
		}
	}

	/**
	 * Creates url to embedded website with given params.
	 *
	 * @param {Array} pathParams
	 * @param {Object<string, *>} queryStringParams
	 * @returns {string}
	 */
	_constructIframeUrl(pathParams = [], queryStringParams = {}) {
		pathParams.unshift(this._service);
		queryStringParams['sentinel'] = this.instanceId;

		const path = pathParams.map(param => encodeURIComponent(param)).join('/');
		const queryString = Object.keys(queryStringParams)
			.filter(
				param =>
					typeof queryStringParams[param] !== 'undefined' &&
					queryStringParams[param] !== null
			)
			.map(param => {
				let value = queryStringParams[param];

				if (typeof value === 'object') {
					value = JSON.stringify(value);
				}

				return `${param}=${encodeURIComponent(value)}`;
			});

		// eslint-disable-next-line prettier/prettier
		return `${this._config.embedUrl}/${path}?${queryString.length ? queryString.join('&') : ''}`;
	}

	_destroyIframe() {
		if (this._wrapper) {
			this._wrapper.innerHTML = '';
			this.iframe = null;
		}
	}

	/**
	 * Returns bounding client rect of the instance iframe
	 *
	 * @returns {DOMRect}
	 */
	_getIframeBoundingClientRect() {
		if (!this.iframe) {
			return {};
		}

		const iframeClientRect = this.iframe.getBoundingClientRect();

		// MS Edge v18 fix
		// post message with payload type ClientRect results into error 80700019
		// we have to copy that into plain object
		return {
			bottom: iframeClientRect.bottom,
			height: iframeClientRect.height,
			left: iframeClientRect.left,
			right: iframeClientRect.right,
			top: iframeClientRect.top,
			width: iframeClientRect.width
		};
	}

	_registerScrollCircle() {
		let lastBoundingRect = '';

		return scrollCircle.register({
			read: () => ({
				window: getWindowViewportRect(),
				iframe: this._getIframeBoundingClientRect()
			}),
			write: ({ payload }) => {
				let boundingRectString;
				const { window, iframe } = payload;

				if ((boundingRectString = JSON.stringify(iframe)) === lastBoundingRect) {
					return;
				}

				this._updateBoundingRectInIframe(window, iframe);

				if (this.addCommentButton) {
					this.addCommentButton.checkVisibility(window, iframe);
				}

				lastBoundingRect = boundingRectString;
			}
		});
	}

	/**
	 * Sends event with iframe bounding rect into the iframe
	 */
	_updateBoundingRectInIframe(window, iframe) {
		this.sendEvent(EmbedScriptEvent.BOUNDING_RECT_CHANGE, { window, iframe });
	}

	_getIdentities() {
		try {
			return window?.sznIVA?.IS?.getIdentities() || {};
		} catch (error) {
			console.error('discussion call sznIVA.IS fail', error);
			// shit happened from a million reasons ...
			return {};
		}
	}

	postComment(data) {
		this.sendEvent(EmbedScriptEvent.POST_COMMENT, data);
	}

	widgetFormClosed() {
		this._componentRoot.remove();

		this._componentRoot = this._createComponentRoot();
		// shadow root must be recreated because it keeps previous widget elements.
		this._componentShadowRoot = this._createComponentShadowRoot(this._componentRoot);

		this.sendEvent(EmbedScriptEvent.WIDGET_FORM_CLOSE);
	}
}
